import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Mail,
  Linkedin,
  Send,
  MessageCircle,
  ArrowRightLeft,
  Ban,
  ChevronDown,
  ChevronRight,
  Link2,
  Clock,
  AlertTriangle,
} from "lucide-react";
import {
  useContactCommsTimeline,
  type CampaignGroup,
  type CommsTimelineEvent,
} from "@/hooks/useContactCommsTimeline";
import {
  useContactSendTimeline,
  groupSendsByMessage,
  type ContactSend,
} from "@/hooks/useContactSendTimeline";

// ── Helpers ──────────────────────────────────────────────────────────

/**
 * Bison returns rendered bodies as HTML. Render them as readable text rather
 * than injecting markup (no dangerouslySetInnerHTML on third-party content).
 */
function htmlToText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>\s*<p>/gi, "\n\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&gt;/g, ">")
    .replace(/&lt;/g, "<")
    .replace(/&amp;/g, "&")
    .replace(/&#39;|&rsquo;/g, "'")
    .replace(/&quot;/g, '"')
    .trim();
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function channelIcon(channel: string | null) {
  if (!channel) return <MessageCircle className="h-3.5 w-3.5" />;
  if (channel.toLowerCase().includes("email"))
    return <Mail className="h-3.5 w-3.5" />;
  return <Linkedin className="h-3.5 w-3.5" />;
}

function statusColor(status: string): string {
  switch (status) {
    case "sent":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "replied":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "draft":
    case "ready_to_send":
    case "loaded":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "bounced":
    case "failed":
      return "bg-red-50 text-red-700 border-red-200";
    case "skipped":
      return "bg-muted text-muted-foreground";
    default:
      return "bg-muted text-muted-foreground";
  }
}

// ── Send Card — one per ACTUAL Bison send ────────────────────────────

/**
 * A single real send. Multi-step Bison sequences produce several of these per
 * message: campaigns 17-20 ran initial + 3d bump + 7d breakup, and steps 2-3
 * carry copy authored INSIDE Bison that exists in no CARR table. Those are
 * badged so nobody mistakes them for CARR-generated copy.
 */
function SendCard({ send }: { send: ContactSend }) {
  const [expand, setExpand] = useState(false);
  const bodyText = send.rendered_body ? htmlToText(send.rendered_body) : null;

  return (
    <div className="rounded-md border bg-background/60 px-2.5 py-2 space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
          SENT
        </Badge>
        {send.step_order != null && (
          <Badge variant="secondary" className="text-[10px]">
            Step {send.step_order}
          </Badge>
        )}
        <span className="text-xs text-muted-foreground">{fmtDate(send.sent_at)}</span>
        {send.thread_reply && (
          <span className="text-[10px] text-muted-foreground">· threaded reply</span>
        )}
      </div>

      {send.is_bison_authored && (
        <div className="flex items-start gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
          <span>
            Bison sequence follow-up — this copy was authored in EmailBison, not in CARR.
          </span>
        </div>
      )}

      {send.actual_subject && (
        <div className="text-xs">
          <span className="text-muted-foreground">Subject: </span>
          <span className="font-medium">{send.actual_subject}</span>
        </div>
      )}

      {bodyText && (
        <div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-muted-foreground"
            onClick={() => setExpand(!expand)}
          >
            {expand ? <ChevronDown className="h-3 w-3 mr-1" /> : <ChevronRight className="h-3 w-3 mr-1" />}
            What was actually sent
          </Button>
          {expand && (
            <div className="mt-1 p-3 rounded-md bg-muted/50 text-xs whitespace-pre-wrap max-h-56 overflow-auto border">
              {bodyText}
            </div>
          )}
        </div>
      )}

      <div className="text-[10px] text-muted-foreground/60 font-mono">
        bison-email:{send.scheduled_email_id}
        {send.bison_campaign_id && <span> · camp:{send.bison_campaign_id}</span>}
      </div>
    </div>
  );
}

// ── Event Card ───────────────────────────────────────────────────────

function EventCard({ event, sends = [] }: { event: CommsTimelineEvent; sends?: ContactSend[] }) {
  const [expandOutbound, setExpandOutbound] = useState(false);
  const [expandReply, setExpandReply] = useState(false);

  // A row with real recorded sends IS sent, even if the parent's sent_at is
  // missing. 166 rows were in exactly that state before the 2026-07-28
  // reconciliation; this keeps a future webhook gap from hiding a real touch.
  const isSent = !!event.sent_at || sends.length > 0;
  const hasReply = !!event.reply_received_at;
  const hasHandoff = !!event.handoff_to_inbox_at;
  const isSkipped = !!event.skipped_at;
  const hasConnection = !!event.connection_accepted_at;

  return (
    <div className="relative pl-6 pb-4 last:pb-0">
      {/* Timeline connector */}
      <div className="absolute left-[9px] top-3 bottom-0 w-px bg-border last:hidden" />
      <div className="absolute left-[5px] top-[10px] h-2.5 w-2.5 rounded-full border-2 border-primary bg-background" />

      <div className="space-y-2">
        {/* Main send event */}
        <div className="flex items-center gap-2 flex-wrap">
          {channelIcon(event.delivery_channel)}
          <Badge variant="outline" className={`text-[11px] ${statusColor(event.status)}`}>
            {event.status.toUpperCase()}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {/* first_sent_at is authoritative: sent_at was historically
                overwritten by each later sequence step. */}
            {isSent ? fmtDate(event.first_sent_at ?? event.sent_at) : fmtDate(event.created_at)}
          </span>
          {sends.length > 1 && (
            <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
              {sends.length} emails sent
            </Badge>
          )}
          <span className="text-xs text-muted-foreground">·</span>
          <span className="text-xs font-medium">{event.message_type?.replace(/_/g, " ")}</span>
          {event.messaging_variant && (
            <Badge variant="secondary" className="text-[10px]">
              {event.messaging_variant}
            </Badge>
          )}
        </div>

        {/* Sender */}
        <div className="text-xs text-muted-foreground flex items-center gap-1">
          <Send className="h-3 w-3" />
          Sender: {event.sender_name || "Unknown"}
        </div>

        {/* CARR-authored copy. When real sends are known they are the source of
            truth for what the prospect received; this is the drafted version. */}
        {event.outbound_text && (
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-muted-foreground"
              onClick={() => setExpandOutbound(!expandOutbound)}
            >
              {expandOutbound ? <ChevronDown className="h-3 w-3 mr-1" /> : <ChevronRight className="h-3 w-3 mr-1" />}
              {sends.length > 0 ? "CARR draft copy" : "Message body"}
            </Button>
            {expandOutbound && (
              <div className="mt-1 p-3 rounded-md bg-muted/50 text-xs whitespace-pre-wrap max-h-48 overflow-auto border">
                {event.outbound_text}
              </div>
            )}
          </div>
        )}

        {/* Actual sends — one card per email EmailBison really delivered. */}
        {sends.length > 0 && (
          <div className="space-y-1.5 pt-0.5">
            <div className="text-[11px] font-medium text-muted-foreground">
              Delivered {sends.length === 1 ? "email" : `emails (${sends.length})`}
            </div>
            {sends.map((s) => (
              <SendCard key={s.id} send={s} />
            ))}
          </div>
        )}

        {/* Connection accepted */}
        {hasConnection && (
          <div className="flex items-center gap-2 text-xs text-blue-700 bg-blue-50 rounded px-2 py-1 w-fit">
            <Link2 className="h-3 w-3" />
            Connection accepted {fmtDate(event.connection_accepted_at)}
          </div>
        )}

        {/* Reply */}
        {hasReply && (
          <div className="ml-2 border-l-2 border-emerald-300 pl-3 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <MessageCircle className="h-3.5 w-3.5 text-emerald-600" />
              <span className="text-xs font-medium text-emerald-700">REPLY</span>
              <span className="text-xs text-muted-foreground">{fmtDate(event.reply_received_at)}</span>
              {event.reply_category && (
                <Badge variant="outline" className="text-[10px]">{event.reply_category}</Badge>
              )}
              {event.reply_sentiment && (
                <Badge variant="secondary" className="text-[10px]">{event.reply_sentiment}</Badge>
              )}
            </div>
            {event.reply_text && (
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs text-muted-foreground"
                  onClick={() => setExpandReply(!expandReply)}
                >
                  {expandReply ? <ChevronDown className="h-3 w-3 mr-1" /> : <ChevronRight className="h-3 w-3 mr-1" />}
                  Reply text
                </Button>
                {expandReply && (
                  <div className="mt-1 p-3 rounded-md bg-emerald-50 text-xs whitespace-pre-wrap max-h-48 overflow-auto border border-emerald-200">
                    {event.reply_text}
                  </div>
                )}
              </div>
            )}
            {/* Reply outcome */}
            {(event.email_reply_outcome || event.linkedin_reply_outcome) && (
              <div className="text-xs text-muted-foreground">
                Outcome: {event.email_reply_outcome || event.linkedin_reply_outcome}
                {(event.email_reply_notes || event.linkedin_reply_notes) && (
                  <span className="ml-1">— {event.email_reply_notes || event.linkedin_reply_notes}</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Handoff */}
        {hasHandoff && (
          <div className="flex items-center gap-2 text-xs text-purple-700 bg-purple-50 rounded px-2 py-1 w-fit">
            <ArrowRightLeft className="h-3 w-3" />
            Handoff: {event.handoff_method?.replace(/_/g, " ")} · {fmtDate(event.handoff_to_inbox_at)}
          </div>
        )}

        {/* Skipped */}
        {isSkipped && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted rounded px-2 py-1 w-fit">
            <Ban className="h-3 w-3" />
            Skipped: {event.skip_reason_category || event.skip_reason || "no reason"}
            {event.skipped_at && <span>· {fmtDate(event.skipped_at)}</span>}
          </div>
        )}

        {/* Trace IDs (subtle) */}
        {(event.bison_lead_id || event.bison_reply_id || event.heyreach_conversation_id) && (
          <div className="text-[10px] text-muted-foreground/60 font-mono">
            {event.bison_lead_id && <span>lead:{event.bison_lead_id} </span>}
            {event.bison_reply_id && <span>reply:{event.bison_reply_id} </span>}
            {event.heyreach_conversation_id && <span>hr:{event.heyreach_conversation_id}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Campaign Group ───────────────────────────────────────────────────

function CampaignGroupCard({
  group,
  sendsByMessage,
}: {
  group: CampaignGroup;
  sendsByMessage: Map<string, ContactSend[]>;
}) {
  const [expanded, setExpanded] = useState(true);

  // Real emails delivered for this campaign. A single message row can map to
  // several sends when the Bison campaign ran a multi-step sequence.
  const sendCount = group.events.reduce(
    (s, e) => s + (sendsByMessage.get(e.id)?.length ?? 0),
    0
  );

  return (
    <Card className="border-dashed">
      <div
        className="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-muted/30"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <span className="text-sm font-medium">
            {group.campaign_name || "Uncategorized"}
          </span>
          <Badge variant="secondary" className="text-[10px]">
            {group.events.length} {group.events.length === 1 ? "message" : "messages"}
          </Badge>
          {sendCount > group.events.length && (
            <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
              {sendCount} emails sent
            </Badge>
          )}
        </div>
        {group.events.some((e) => e.reply_received_at) && (
          <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
            Has reply
          </Badge>
        )}
      </div>
      {expanded && (
        <CardContent className="px-4 pb-4 pt-0">
          {group.events.map((ev) => (
            <EventCard key={ev.id} event={ev} sends={sendsByMessage.get(ev.id) ?? []} />
          ))}
        </CardContent>
      )}
    </Card>
  );
}

// ── Main Tab ─────────────────────────────────────────────────────────

interface ContactCommsTabProps {
  contactId: string | null;
}

export function ContactCommsTab({ contactId }: ContactCommsTabProps) {
  const { data: groups, isLoading, error } = useContactCommsTimeline(contactId);
  // Per-send history. Loads in parallel; the timeline still renders without it.
  const { data: sends } = useContactSendTimeline(contactId);

  const sendsByMessage = useMemo(
    () => groupSendsByMessage(sends ?? []),
    [sends]
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-destructive">
        Failed to load comms timeline: {(error as Error).message}
      </div>
    );
  }

  if (!groups || groups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <Clock className="h-8 w-8 mb-2" />
        <p className="text-sm font-medium">No outreach history</p>
        <p className="text-xs mt-1">No messages have been sent to this contact yet.</p>
      </div>
    );
  }

  const totalMessages = groups.reduce((s, g) => s + g.events.length, 0);
  const totalReplies = groups.reduce(
    (s, g) => s + g.events.filter((e) => e.reply_received_at).length,
    0
  );
  // Actual emails delivered. Higher than totalMessages wherever a multi-step
  // Bison sequence fired follow-ups off a single CARR message row.
  const totalSends = sends?.length ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h3 className="text-sm font-semibold">Comms Timeline</h3>
        <Badge variant="secondary" className="text-[11px]">
          {totalMessages} messages
        </Badge>
        {totalSends > totalMessages && (
          <Badge variant="outline" className="text-[11px] bg-blue-50 text-blue-700 border-blue-200">
            {totalSends} emails sent
          </Badge>
        )}
        {totalReplies > 0 && (
          <Badge variant="outline" className="text-[11px] bg-emerald-50 text-emerald-700 border-emerald-200">
            {totalReplies} {totalReplies === 1 ? "reply" : "replies"}
          </Badge>
        )}
      </div>

      {groups.map((group) => (
        <CampaignGroupCard
          key={group.campaign_id ?? "__uncategorized__"}
          group={group}
          sendsByMessage={sendsByMessage}
        />
      ))}
    </div>
  );
}
